import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Plus, Trash2, Search, Brain, MessageSquare, Users, Activity, ArrowLeft, Tag, Zap, Database } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TrainingMode() {
  const [activeTab, setActiveTab] = useState("kb-docs");
  const [kbDocs, setKbDocs] = useState([]);
  const [intentExamples, setIntentExamples] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [showKbForm, setShowKbForm] = useState(false);
  const [showIntentForm, setShowIntentForm] = useState(false);
  const [kbForm, setKbForm] = useState({ title: "", content: "", tags: "" });
  const [intentForm, setIntentForm] = useState({ 
    utterance: "", 
    intent: "", 
    entities: "{}", 
    weight: "1.0" 
  });
  
  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch data
  const fetchKbDocs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/kb-docs`);
      setKbDocs(response.data.docs || []);
    } catch (error) {
      console.error("Error fetching KB docs:", error);
    }
  }, []);

  const fetchIntentExamples = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/intent-examples`);
      setIntentExamples(response.data.examples || []);
    } catch (error) {
      console.error("Error fetching intent examples:", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchKbDocs(), fetchIntentExamples(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchKbDocs, fetchIntentExamples, fetchStats]);

  // Create KB Doc
  const handleCreateKbDoc = async (e) => {
    e.preventDefault();
    if (!kbForm.title.trim() || !kbForm.content.trim()) {
      toast.error("Preencha título e conteúdo");
      return;
    }

    try {
      const tags = kbForm.tags.split(",").map(t => t.trim()).filter(t => t);
      await axios.post(`${API}/admin/kb-docs`, {
        title: kbForm.title,
        content: kbForm.content,
        tags
      });
      toast.success("Documento criado!");
      setKbForm({ title: "", content: "", tags: "" });
      setShowKbForm(false);
      fetchKbDocs();
      fetchStats();
    } catch (error) {
      console.error("Error creating KB doc:", error);
      toast.error("Erro ao criar documento");
    }
  };

  // Delete KB Doc
  const handleDeleteKbDoc = async (docId) => {
    try {
      await axios.delete(`${API}/admin/kb-docs/${docId}`);
      toast.success("Documento removido!");
      fetchKbDocs();
      fetchStats();
    } catch (error) {
      console.error("Error deleting KB doc:", error);
      toast.error("Erro ao remover documento");
    }
  };

  // Create Intent Example
  const handleCreateIntentExample = async (e) => {
    e.preventDefault();
    if (!intentForm.utterance.trim() || !intentForm.intent.trim()) {
      toast.error("Preencha frase e intent");
      return;
    }

    try {
      let entities = {};
      try {
        entities = JSON.parse(intentForm.entities || "{}");
      } catch {
        toast.error("Entidades devem ser JSON válido");
        return;
      }

      await axios.post(`${API}/admin/intent-examples`, {
        utterance: intentForm.utterance,
        intent: intentForm.intent,
        entities,
        weight: parseFloat(intentForm.weight) || 1.0
      });
      toast.success("Exemplo criado!");
      setIntentForm({ utterance: "", intent: "", entities: "{}", weight: "1.0" });
      setShowIntentForm(false);
      fetchIntentExamples();
      fetchStats();
    } catch (error) {
      console.error("Error creating intent example:", error);
      toast.error("Erro ao criar exemplo");
    }
  };

  // Delete Intent Example
  const handleDeleteIntentExample = async (exampleId) => {
    try {
      await axios.delete(`${API}/admin/intent-examples/${exampleId}`);
      toast.success("Exemplo removido!");
      fetchIntentExamples();
      fetchStats();
    } catch (error) {
      console.error("Error deleting intent example:", error);
      toast.error("Erro ao remover exemplo");
    }
  };

  // Filter data based on search
  const filteredKbDocs = kbDocs.filter(doc => 
    doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredIntentExamples = intentExamples.filter(ex =>
    ex.utterance?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ex.intent?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Intent colors
  const getIntentColor = (intent) => {
    const colors = {
      "reminder.create": "bg-blue-500/20 text-blue-400 border-blue-500/30",
      "task.create": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      "task.list": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
      "task.complete": "bg-green-500/20 text-green-400 border-green-500/30",
      "habit.create": "bg-purple-500/20 text-purple-400 border-purple-500/30",
      "habit.checkin": "bg-violet-500/20 text-violet-400 border-violet-500/30",
      "finance.add_expense": "bg-red-500/20 text-red-400 border-red-500/30",
      "finance.add_income": "bg-lime-500/20 text-lime-400 border-lime-500/30",
      "finance.summary": "bg-amber-500/20 text-amber-400 border-amber-500/30",
      "note.create": "bg-pink-500/20 text-pink-400 border-pink-500/30",
      "undo": "bg-slate-500/20 text-slate-400 border-slate-500/30",
      "greeting": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
    };
    return colors[intent] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0B10] flex items-center justify-center">
        <div className="text-cyan-400 font-mono">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0B10] text-slate-200">
      {/* Header */}
      <header className="glass border-b border-slate-800/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              className="flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft size={18} />
              <span className="text-sm font-mono">VOLTAR</span>
            </a>
            <div className="w-px h-6 bg-slate-700"></div>
            <div className="flex items-center gap-3">
              <Brain className="text-cyan-400" size={24} />
              <h1 className="font-rajdhani text-xl font-semibold tracking-wide">MODO TREINO</h1>
            </div>
          </div>
          
          {/* Stats — backend /admin/stats retorna shape nested:
              { knowledge_base:{total_docs,tags}, intent_examples:{total_examples,intents},
                user_memories:{total_users}, sessions:{total_sessions} }
              Antes o frontend esperava o shape flat antigo e renderizava o
              objeto inteiro como child (React crash "Objects are not valid").
           */}
          {stats && (
            <div className="flex items-center gap-6 text-xs font-mono">
              <div className="flex items-center gap-2">
                <Database size={14} className="text-cyan-400" />
                <span className="text-slate-400">KB:</span>
                <span className="text-slate-200">{stats.knowledge_base?.total_docs ?? 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare size={14} className="text-purple-400" />
                <span className="text-slate-400">Exemplos:</span>
                <span className="text-slate-200">{stats.intent_examples?.total_examples ?? 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users size={14} className="text-emerald-400" />
                <span className="text-slate-400">Usuários:</span>
                <span className="text-slate-200">{stats.user_memories?.total_users ?? 0}</span>
              </div>
              <div className="flex items-center gap-2 px-2 py-1 rounded bg-slate-800/60 text-slate-300">
                <Zap size={12} />
                <span>{stats.sessions?.total_sessions ?? 0} sessões</span>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab("kb-docs")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-sm transition-all ${
              activeTab === "kb-docs"
                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                : "bg-slate-800/50 text-slate-400 border border-slate-700/30 hover:text-slate-200"
            }`}
            data-testid="tab-kb-docs"
          >
            <BookOpen size={16} />
            KNOWLEDGE BASE
          </button>
          <button
            onClick={() => setActiveTab("intent-examples")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-sm transition-all ${
              activeTab === "intent-examples"
                ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                : "bg-slate-800/50 text-slate-400 border border-slate-700/30 hover:text-slate-200"
            }`}
            data-testid="tab-intent-examples"
          >
            <MessageSquare size={16} />
            EXEMPLOS DE INTENT
          </button>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar..."
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={() => activeTab === "kb-docs" ? setShowKbForm(true) : setShowIntentForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-mono text-sm hover:bg-cyan-500/30 transition-all"
            data-testid="add-button"
          >
            <Plus size={16} />
            {activeTab === "kb-docs" ? "NOVO DOCUMENTO" : "NOVO EXEMPLO"}
          </button>
        </div>

        {/* KB Docs Tab */}
        {activeTab === "kb-docs" && (
          <div className="space-y-4">
            {/* Add Form */}
            <AnimatePresence>
              {showKbForm && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  onSubmit={handleCreateKbDoc}
                  className="glass rounded-lg p-4 space-y-4"
                  data-testid="kb-form"
                >
                  <div>
                    <label className="text-xs text-slate-400 font-mono mb-1 block">TÍTULO</label>
                    <input
                      type="text"
                      value={kbForm.title}
                      onChange={(e) => setKbForm({...kbForm, title: e.target.value})}
                      placeholder="Ex: Horários Padrão"
                      className="w-full bg-slate-800/50 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono mb-1 block">CONTEÚDO</label>
                    <textarea
                      value={kbForm.content}
                      onChange={(e) => setKbForm({...kbForm, content: e.target.value})}
                      placeholder="Regras ou conhecimento que o Nexo deve saber..."
                      rows={4}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono mb-1 block">TAGS (separadas por vírgula)</label>
                    <input
                      type="text"
                      value={kbForm.tags}
                      onChange={(e) => setKbForm({...kbForm, tags: e.target.value})}
                      placeholder="Ex: horário, data, tempo"
                      className="w-full bg-slate-800/50 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" className="px-4 py-2 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-mono text-sm hover:bg-cyan-500/30">
                      SALVAR
                    </button>
                    <button type="button" onClick={() => setShowKbForm(false)} className="px-4 py-2 rounded bg-slate-800/50 text-slate-400 border border-slate-700/30 font-mono text-sm hover:text-slate-200">
                      CANCELAR
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>

            {/* List */}
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {filteredKbDocs.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen size={32} className="mx-auto text-slate-600 mb-3" />
                    <p className="text-slate-500 text-sm">Nenhum documento encontrado</p>
                  </div>
                ) : (
                  filteredKbDocs.map((doc) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="glass rounded-lg p-4 group"
                      data-testid={`kb-doc-${doc.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-slate-200">{doc.title}</h3>
                        <button
                          onClick={() => handleDeleteKbDoc(doc.id)}
                          className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <p className="text-sm text-slate-400 mb-3">{doc.content}</p>
                      {doc.tags && doc.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {doc.tags.map((tag, i) => (
                            <span key={i} className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800/50 text-slate-400 text-xs font-mono border border-slate-700/50">
                              <Tag size={10} />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Intent Examples Tab */}
        {activeTab === "intent-examples" && (
          <div className="space-y-4">
            {/* Add Form */}
            <AnimatePresence>
              {showIntentForm && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  onSubmit={handleCreateIntentExample}
                  className="glass rounded-lg p-4 space-y-4"
                  data-testid="intent-form"
                >
                  <div>
                    <label className="text-xs text-slate-400 font-mono mb-1 block">FRASE (utterance)</label>
                    <input
                      type="text"
                      value={intentForm.utterance}
                      onChange={(e) => setIntentForm({...intentForm, utterance: e.target.value})}
                      placeholder="Ex: me lembra de tomar remédio às 14h"
                      className="w-full bg-slate-800/50 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-400 font-mono mb-1 block">INTENT</label>
                      <select
                        value={intentForm.intent}
                        onChange={(e) => setIntentForm({...intentForm, intent: e.target.value})}
                        className="w-full bg-slate-800/50 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/50 focus:outline-none"
                      >
                        <option value="">Selecione...</option>
                        <optgroup label="Lembretes">
                          <option value="reminder.create">reminder.create</option>
                          <option value="reminder.list">reminder.list</option>
                          <option value="reminder.complete">reminder.complete</option>
                          <option value="reminder.snooze">reminder.snooze</option>
                        </optgroup>
                        <optgroup label="Tarefas">
                          <option value="task.create">task.create</option>
                          <option value="task.list">task.list</option>
                          <option value="task.complete">task.complete</option>
                          <option value="task.update_status">task.update_status</option>
                          <option value="task.postpone">task.postpone</option>
                        </optgroup>
                        <optgroup label="Hábitos">
                          <option value="habit.create">habit.create</option>
                          <option value="habit.checkin">habit.checkin</option>
                          <option value="habit.list">habit.list</option>
                          <option value="habit.stats">habit.stats</option>
                        </optgroup>
                        <optgroup label="Financeiro">
                          <option value="finance.add_expense">finance.add_expense</option>
                          <option value="finance.add_income">finance.add_income</option>
                          <option value="finance.summary">finance.summary</option>
                          <option value="finance.list">finance.list</option>
                        </optgroup>
                        <optgroup label="Notas">
                          <option value="note.create">note.create</option>
                          <option value="note.list">note.list</option>
                          <option value="note.search">note.search</option>
                        </optgroup>
                        <optgroup label="Outros">
                          <option value="undo">undo</option>
                          <option value="greeting">greeting</option>
                          <option value="question">question</option>
                          <option value="dashboard">dashboard</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 font-mono mb-1 block">PESO (1.0 = normal, 2.0 = correção)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="5"
                        value={intentForm.weight}
                        onChange={(e) => setIntentForm({...intentForm, weight: e.target.value})}
                        className="w-full bg-slate-800/50 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/50 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono mb-1 block">ENTIDADES (JSON)</label>
                    <textarea
                      value={intentForm.entities}
                      onChange={(e) => setIntentForm({...intentForm, entities: e.target.value})}
                      placeholder='{"message": "tomar remédio", "time": "14:00"}'
                      rows={2}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none font-mono"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" className="px-4 py-2 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono text-sm hover:bg-purple-500/30">
                      SALVAR
                    </button>
                    <button type="button" onClick={() => setShowIntentForm(false)} className="px-4 py-2 rounded bg-slate-800/50 text-slate-400 border border-slate-700/30 font-mono text-sm hover:text-slate-200">
                      CANCELAR
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>

            {/* Intent Distribution — backend retorna apenas a lista de intents
                distintos (stats.intent_examples.intents). Não tem contagem
                por intent disponível — se precisar, adicionar agg no backend. */}
            {stats?.intent_examples?.intents?.length > 0 && (
              <div className="glass rounded-lg p-4 mb-4">
                <h3 className="text-xs text-slate-400 font-mono mb-3 flex items-center gap-2">
                  <Activity size={14} />
                  INTENTS CATALOGADOS ({stats.intent_examples.intents.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {stats.intent_examples.intents.slice(0, 30).map((intent, i) => (
                    <span key={i} className={`px-2 py-1 rounded text-xs font-mono border ${getIntentColor(intent)}`}>
                      {intent}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* List */}
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {filteredIntentExamples.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare size={32} className="mx-auto text-slate-600 mb-3" />
                    <p className="text-slate-500 text-sm">Nenhum exemplo encontrado</p>
                  </div>
                ) : (
                  filteredIntentExamples.map((ex) => (
                    <motion.div
                      key={ex.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="glass rounded-lg p-3 group flex items-center justify-between"
                      data-testid={`intent-example-${ex.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-mono border ${getIntentColor(ex.intent)}`}>
                            {ex.intent}
                          </span>
                          {ex.weight > 1 && (
                            <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs font-mono border border-amber-500/30">
                              peso: {ex.weight}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-300">"{ex.utterance}"</p>
                        {ex.entities && Object.keys(ex.entities).length > 0 && (
                          <p className="text-xs text-slate-500 font-mono mt-1">
                            {JSON.stringify(ex.entities)}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteIntentExample(ex.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all ml-4"
                      >
                        <Trash2 size={16} />
                      </button>
                    </motion.div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </main>
    </div>
  );
}
